import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Delete, Loader2, Mail, KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function PlannerLogin({ redirectTo = "/planner", appName = "NH Planner", subtitle = "Agenda & To-Do's" }) {
  const { login, loginPin } = useAuth();
  const navigate = useNavigate();
  const [pinEnabled, setPinEnabled] = useState(null);
  const [mode, setMode] = useState("pin"); // pin | email
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = appName;
    api.get("/auth/pin-status")
      .then((r) => { setPinEnabled(r.data.pin_enabled); setMode(r.data.pin_enabled ? "pin" : "email"); })
      .catch(() => { setPinEnabled(false); setMode("email"); });
  }, []);

  const submitPin = async (code) => {
    setLoading(true);
    try { await loginPin(code); navigate(redirectTo); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPin(""); }
    finally { setLoading(false); }
  };

  const tap = (d) => { if (loading) return; setPin((p) => (p + d).slice(0, 6)); };
  const back = () => setPin((p) => p.slice(0, -1));

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); navigate(redirectTo); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream-admin flex items-center justify-center px-5" data-testid="planner-login">
      <div className="w-full max-w-xs">
        <div className="text-center mb-8">
          <img src="/logo-round.png" alt="NH Planner" className="mx-auto w-16 h-16 rounded-2xl" />
          <h1 className="mt-3 font-display text-2xl text-ink">{appName}</h1>
          <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>
        </div>

        {mode === "pin" && pinEnabled && (
          <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.06)]" data-testid="planner-pin-pad">
            <div className="flex justify-center gap-3 mb-6 h-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <span key={i} className={`w-3 h-3 rounded-full transition-colors ${i < pin.length ? "bg-brand" : "bg-cream-subtle border border-[#E8E2D9]"} ${i >= 4 && pin.length < 5 ? "opacity-40" : ""}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => (
                <button key={n} onClick={() => tap(n)} data-testid={`pin-key-${n}`}
                  className="h-14 rounded-xl bg-cream-subtle text-xl font-display text-ink active:scale-95 hover:bg-brand-light transition-all">{n}</button>
              ))}
              <button onClick={() => { setMode("email"); setPin(""); }} data-testid="pin-to-email"
                className="h-14 rounded-xl text-ink-muted hover:text-brand flex items-center justify-center transition-colors" title="Inloggen met e-mail"><Mail className="w-5 h-5" /></button>
              <button onClick={() => tap("0")} data-testid="pin-key-0"
                className="h-14 rounded-xl bg-cream-subtle text-xl font-display text-ink active:scale-95 hover:bg-brand-light transition-all">0</button>
              <button onClick={back} data-testid="pin-key-back"
                className="h-14 rounded-xl text-ink-muted hover:text-brand flex items-center justify-center transition-colors"><Delete className="w-5 h-5" /></button>
            </div>
            <Button onClick={() => submitPin(pin)} disabled={loading || pin.length < 4} data-testid="pin-submit"
              className="w-full mt-5 rounded-full bg-brand hover:bg-brand-hover text-white h-12">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Inloggen"}
            </Button>
          </div>
        )}

        {mode === "email" && (
          <form onSubmit={submitEmail} data-testid="planner-email-form"
            className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.06)] space-y-4">
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">E-mail</Label>
              <Input data-testid="planner-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border-[#E8E2D9]" placeholder="admin@nellekeholt.nl" />
            </div>
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Wachtwoord</Label>
              <Input data-testid="planner-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl border-[#E8E2D9]" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="planner-login-submit"
              className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Inloggen"}
            </Button>
            {pinEnabled && (
              <button type="button" data-testid="email-to-pin" onClick={() => { setMode("pin"); setPassword(""); }}
                className="w-full flex items-center justify-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors">
                <KeyRound className="w-4 h-4" /> Inloggen met pincode
              </button>
            )}
            {!pinEnabled && (
              <p className="text-center text-xs text-ink-muted">Wil je met een pincode inloggen? Stel er eerst één in bij <span className="text-ink">Beheer &rarr; Instellingen</span>.</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
